import styled from '@emotion/styled';
import React from 'react';

import type { LensDivProps, ResponsiveType } from '../../types';
import { getResponsiveOneOf } from '../../utilities';

const alignmentsPlaceItems = {
  topLeft: 'start',
  topCenter: 'start center',
  topRight: 'start end',
  centerLeft: 'center start',
  center: 'center',
  centerRight: 'center end',
  bottomLeft: 'end start',
  bottomCenter: 'end center',
  bottomRight: 'end',
};

const getPlaceItems = (
  alignment: ResponsiveType<
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'centerLeft'
    | 'center'
    | 'centerRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
  >
) => {
  if (Array.isArray(alignment)) {
    return alignment.map(
      alignmentArrayItem => alignmentsPlaceItems[alignmentArrayItem]
    );
  }

  if (typeof alignment === 'object') {
    const alignmentsPlaceItemsObject = {};

    Object.entries(alignment).forEach(([key, value]) => {
      return (alignmentsPlaceItemsObject[key] = alignmentsPlaceItems[value]);
    });

    return alignmentsPlaceItemsObject;
  }

  return alignmentsPlaceItems[alignment];
};

const AlignWrapper = styled.div<AlignWrapperProps>`
  width: 100%;
  height: 100%;
  display: grid;
  ${props => getResponsiveOneOf('place-items', getPlaceItems(props.alignment))};
`;

const Align = ({
  children,
  alignment = 'center',
  htmlTag = 'div',
  ...props
}: AlignProps &
  React.ComponentProps<typeof AlignWrapper> &
  Omit<
    LensDivProps,
    keyof AlignProps & React.ComponentProps<typeof AlignWrapper>
  >) => {
  return (
    <AlignWrapper alignment={alignment} as={htmlTag} {...props}>
      {children}
    </AlignWrapper>
  );
};

export const availableAlignments = Object.keys(alignmentsPlaceItems);
export const availableHtmlTags = [
  'div',
  'header',
  'article',
  'section',
  'nav',
  'aside',
  'footer',
  'main',
];

type AlignProps = {
  children: React.ReactNode;
  alignment?: ResponsiveType<
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'centerLeft'
    | 'center'
    | 'centerRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
  >;
  htmlTag?:
    | 'div'
    | 'header'
    | 'article'
    | 'section'
    | 'nav'
    | 'aside'
    | 'footer'
    | 'main';
};

type AlignWrapperProps = {
  children: React.ReactNode;
  alignment?: ResponsiveType<
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'centerLeft'
    | 'center'
    | 'centerRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
  >;
  as?: string;
};

export default Align;
